import {
	Snackbar,
	SnackbarTrigger,
	type SnackbarProps,
} from '@px-center/snackbar';
import { createContext, type ReactNode } from 'react';

type ToastProps = {
	title?: string | number;
	message?: string | number;
	miliseconds?: number;
	mode?: SnackbarProps['typeMessage'] | 'error';
};

type Nullish = null | undefined;

type AlertProviderProps = {
	children?: ReactNode;
};

type ToastOptions = ToastProps | Nullish;

type Context = {
	toast: (options: ToastOptions) => void;
};

export const AlertContext = createContext({} as Context);

export const AlertProvider = ({ children }: AlertProviderProps) => {
	const handleToast = (options: ToastOptions) => {
		const {
			title: providedTitle,
			message,
			mode = 'info',
			miliseconds = 3500,
		} = options || {};

		const { title, description } = getToastContent(providedTitle, message);

		const { dismiss } = SnackbarTrigger({
			'data-test-qa-id': `snackbar-${title}`,
			description,
			title,
			typeMessage: mode === 'error' ? 'danger' : mode,
			showIcon: true,
		});

		setTimeout(dismiss, miliseconds);
	};

	const getToastContent = (
		providedTitle?: string | number,
		message?: string | number,
	) => {
		if (providedTitle) {
			return {
				title: String(providedTitle),
				description: message ? String(message) : undefined,
			};
		}

		if (message) {
			return {
				title: String(message),
				description: undefined,
			};
		}

		return {
			title: '',
			description: undefined,
		};
	};

	return (
		<AlertContext.Provider value={{ toast: handleToast }}>
			<div className="max-sm:[&_ol]:pt-[calc(1rem+env(safe-area-inset-top,0px))]">
				<Snackbar />
			</div>
			{children}
		</AlertContext.Provider>
	);
};
